/**
 * Crowdsourced pin-location strip. Three states:
 *
 *  1. Hole has no GPS pin → "Help us by saving the pin" CTA always visible
 *     once the user has a GPS fix.
 *  2. Hole has a pin AND the user is within 25 yds of it (i.e. on the green)
 *     → "Confirm pin location" CTA + the running count of confirmations.
 *  3. Anywhere else → tiny "Pin confirmed by N players" badge if N > 0;
 *     hidden entirely otherwise.
 *
 * Three player confirmations within 10 yds of each other auto-promote the
 * median fix to the official pin (backend handles the clustering).
 */
import { useEffect, useState } from "react";
import { Check, MapPin } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";

export default function PinConfirmStrip({ courseId, holeNumber, userPos, gpsAccuracy, distance, distanceSource }) {
  const [status, setStatus] = useState(null); // { has_pin, pin_confirmed_by, user_has_confirmed }
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  // Reload status whenever the hole changes.
  useEffect(() => {
    let cancelled = false;
    if (!courseId || holeNumber == null) return undefined;
    (async () => {
      try {
        const { data } = await api.get(
          `/courses/${courseId}/holes/${holeNumber}/pin-status`
        );
        if (!cancelled) setStatus(data);
      } catch {
        if (!cancelled) setStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [courseId, holeNumber]);

  if (!status) return null;

  const onTheGreen =
    status.has_pin &&
    distanceSource === "gps" &&
    typeof distance === "number" &&
    distance <= 25;
  const needsInitialPin = !status.has_pin;
  const canConfirm = (needsInitialPin || onTheGreen) && userPos;
  const accuracyOk = gpsAccuracy == null || gpsAccuracy <= 25;

  const submit = async () => {
    if (!userPos || busy) return;
    setBusy(true);
    setToast(null);
    try {
      const { data } = await api.post(
        `/courses/${courseId}/holes/${holeNumber}/confirm-pin`,
        { lat: userPos.lat, lng: userPos.lng, accuracy: gpsAccuracy ?? null }
      );
      setStatus((s) => ({
        ...(s || {}),
        has_pin: data.locked || (s?.has_pin ?? false),
        pin_confirmed_by: data.locked ? data.confirmations : (s?.pin_confirmed_by || 0),
        user_has_confirmed: true,
      }));
      if (data.locked) {
        setToast("🎯 Pin locked in! Thanks for helping.");
      } else if (data.needed > 0) {
        setToast(`Saved · ${data.needed} more needed to lock this pin in`);
      } else {
        setToast("Saved · thanks!");
      }
      setTimeout(() => setToast(null), 2800);
    } catch (err) {
      setToast(formatApiError(err));
      setTimeout(() => setToast(null), 3000);
    } finally {
      setBusy(false);
    }
  };

  // Show only the small badge if nothing actionable.
  if (!canConfirm && (status.pin_confirmed_by || 0) > 0) {
    return (
      <div
        data-testid="pin-confirmed-badge"
        className="mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold"
        style={{ background: "var(--accent-turf)", color: "var(--primary)" }}
      >
        <Check size={11} weight="bold" />
        Pin confirmed by {status.pin_confirmed_by} player{status.pin_confirmed_by === 1 ? "" : "s"}
      </div>
    );
  }

  if (!canConfirm) return null;

  return (
    <div
      data-testid="pin-confirm-strip"
      className="mt-3 rounded-2xl px-4 py-3"
      style={{ background: "var(--accent-sand)", border: "1.5px solid var(--border)" }}
    >
      <div className="flex items-center gap-3">
        <div
          className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "var(--primary)", color: "#F2EEE7" }}
        >
          <MapPin size={18} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-sm text-[var(--primary)] leading-tight">
            {needsInitialPin ? "Help us pin this hole" : "On the green?"}
          </div>
          <div className="text-[11px] text-[var(--text-secondary)] leading-snug">
            {needsInitialPin
              ? "No pin yet — tap to save where you're standing as the green."
              : status.user_has_confirmed
                ? `You've confirmed · ${status.pin_confirmed_by || 0} player${(status.pin_confirmed_by || 0) === 1 ? "" : "s"} agree so far`
                : "Tap to confirm the pin location for every player."}
          </div>
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={busy || !accuracyOk || status.user_has_confirmed}
          data-testid="pin-confirm-button"
          className="text-[11px] uppercase tracking-wider font-bold px-3 py-2 rounded-full transition-colors"
          style={{
            background: status.user_has_confirmed ? "var(--bg-app)" : "var(--primary)",
            color: status.user_has_confirmed ? "var(--text-secondary)" : "#F2EEE7",
            opacity: !accuracyOk || busy ? 0.55 : 1,
          }}
        >
          {status.user_has_confirmed
            ? "Done ✓"
            : busy
              ? "Saving…"
              : !accuracyOk
                ? "Weak GPS"
                : "Confirm"}
        </button>
      </div>
      {toast && (
        <div className="mt-2 text-[11px] text-[var(--primary)] font-medium" data-testid="pin-confirm-toast">
          {toast}
        </div>
      )}
    </div>
  );
}
